// E-mail accounts beheren. PRD-08 §6 + PRD-03 §3.12.

import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

interface EmailAccount {
  id: string;
  email: string;
  provider: "GMAIL" | "OUTLOOK" | "ZIGGO" | "KPN" | "IMAP_CUSTOM";
  status: "ACTIVE" | "ERROR" | "DISABLED" | "SYNCING" | "PENDING_AUTH";
  lastSyncAt: string | null;
  lastSyncError: string | null;
  syncErrorCount: number;
}

const PROVIDERS: EmailAccount["provider"][] = ["OUTLOOK", "ZIGGO", "KPN", "IMAP_CUSTOM"];

export function EmailAccountsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const gmailStatus = searchParams.get("gmail");
  const gmailErrorCode = searchParams.get("code");

  useEffect(() => {
    if (gmailStatus === "connected") {
      qc.invalidateQueries({ queryKey: ["email-accounts"] });
    }
  }, [gmailStatus, qc]);

  const gmailConnect = useMutation({
    mutationFn: async () => {
      const { authUrl } = await api.get<{ authUrl: string }>("/v1/auth/gmail/authorize");
      window.location.href = authUrl;
    },
    // De fout staat hieronder al onder de knop, zie `gmailConnect.error`. Dit is
    // de enige mutatie in de applicatie die dat zelf doet; alle andere leunen op
    // de banner uit MutationErrorBanner.
    meta: { foutInBeeld: true },
  });

  const accountsQuery = useQuery({
    queryKey: ["email-accounts"],
    queryFn: () => api.get<{ accounts: EmailAccount[] }>("/v1/email-accounts"),
  });

  const [provider, setProvider] = useState<EmailAccount["provider"]>("OUTLOOK");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/v1/email-accounts", {
        provider,
        email,
        password,
        ...(provider === "IMAP_CUSTOM" ? { imapHost, imapPort } : {}),
      }),
    onSuccess: () => {
      setEmail("");
      setPassword("");
      setImapHost("");
      qc.invalidateQueries({ queryKey: ["email-accounts"] });
    },
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  const sync = useMutation({
    mutationFn: (id: string) => api.post(`/v1/email-accounts/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-accounts"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/email-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-accounts"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorKey(null);
    create.mutate();
  }

  return (
    <main className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold">{t("email.settings.title", "E-mail koppelen")}</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm text-neutral-600 hover:text-neutral-900"
          >
            ← {t("back", "Terug")}
          </button>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {gmailStatus === "connected" && (
          <div
            role="status"
            className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-900 flex items-center justify-between"
          >
            <span>{t("email.settings.gmail.success", "Gmail succesvol gekoppeld!")}</span>
            <button
              onClick={() => setSearchParams({})}
              className="text-xs text-green-700 hover:text-green-900 underline"
            >
              {t("dismiss", "Sluit")}
            </button>
          </div>
        )}
        {gmailStatus === "error" && (
          <div
            role="alert"
            className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900"
          >
            {t("email.settings.gmail.error", "Gmail-koppeling mislukt.")}{" "}
            {gmailErrorCode && <code className="text-xs">({gmailErrorCode})</code>}
          </div>
        )}
        {gmailStatus === "denied" && (
          <div
            role="alert"
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900"
          >
            {t("email.settings.gmail.denied", "Toestemming geweigerd. Je kunt opnieuw verbinden.")}
          </div>
        )}

        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-semibold mb-1">
            {t("email.settings.gmail.title", "Gmail-koppeling")}
          </h2>
          <p className="text-sm text-neutral-600 mb-4">
            {t(
              "email.settings.gmail.intro",
              "Veiligste manier om Gmail te koppelen — via Google's eigen autorisatiescherm. Geen wachtwoord delen."
            )}
          </p>
          <button
            type="button"
            onClick={() => gmailConnect.mutate()}
            disabled={gmailConnect.isPending}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-5 py-2.5 bg-white border border-neutral-300 hover:bg-neutral-50 disabled:opacity-60 text-neutral-800 font-medium rounded-lg shadow-sm"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.7 1.22 9.18 3.6l6.85-6.84C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            <span>
              {gmailConnect.isPending
                ? t("email.settings.gmail.connecting", "Bezig…")
                : t("email.settings.gmail.connect", "Verbinden via Google")}
            </span>
          </button>
          {gmailConnect.error instanceof ApiError && (
            <p className="mt-2 text-sm text-red-700">{t(gmailConnect.error.i18nKey)}</p>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-semibold mb-4">
            {t("email.settings.add", "Account toevoegen")}
          </h2>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("email.settings.providerLabel", "Provider")}
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as EmailAccount["provider"])}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white"
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("email.settings.emailLabel", "E-mailadres")}
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("email.settings.passwordLabel", "Wachtwoord")}
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
            </div>
            {provider === "IMAP_CUSTOM" && (
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">IMAP host</label>
                  <input
                    type="text"
                    required
                    value={imapHost}
                    onChange={(e) => setImapHost(e.target.value)}
                    placeholder="imap.example.com"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Port</label>
                  <input
                    type="number"
                    required
                    value={imapPort}
                    onChange={(e) => setImapPort(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  />
                </div>
              </div>
            )}
            {errorKey && (
              <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                {t(errorKey)}
              </div>
            )}
            <button
              type="submit"
              disabled={create.isPending}
              className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
            >
              {create.isPending
                ? t("email.settings.testing", "Verbinding testen…")
                : t("email.settings.connect", "Verbinden")}
            </button>
            <p className="text-xs text-neutral-500">
              {t(
                "email.settings.hint",
                "We testen je verbinding direct. Bij 2FA-accounts: maak een app-password aan in de provider-instellingen."
              )}
            </p>
          </form>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <header className="px-5 py-3 border-b border-neutral-200">
            <h2 className="font-semibold">{t("email.settings.connected", "Gekoppelde accounts")}</h2>
          </header>
          {accountsQuery.isLoading ? (
            <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
          ) : accountsQuery.data?.accounts.length === 0 ? (
            <p className="p-5 text-sm text-neutral-500">
              {t("email.settings.empty", "Nog geen accounts gekoppeld.")}
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {accountsQuery.data?.accounts.map((a) => (
                <li key={a.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{a.email}</p>
                    <p className="text-xs text-neutral-500">
                      {a.provider} ·{" "}
                      <span
                        className={
                          a.status === "ERROR"
                            ? "text-red-700"
                            : a.status === "SYNCING"
                              ? "text-amber-700"
                              : "text-green-700"
                        }
                      >
                        {a.status}
                      </span>
                      {a.lastSyncAt && ` · ${new Date(a.lastSyncAt).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}`}
                    </p>
                    {a.lastSyncError && (
                      <p className="text-xs text-red-600 mt-1">{a.lastSyncError}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => sync.mutate(a.id)}
                      disabled={sync.isPending}
                      className="text-sm text-brand-600 hover:text-brand-800"
                    >
                      {t("email.settings.sync", "Sync nu")}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(t("email.settings.confirmRemove", "Account ontkoppelen?"))) {
                          remove.mutate(a.id);
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      {t("email.settings.remove", "Ontkoppelen")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-sm">
          <Link to="/emails" className="text-brand-600 hover:text-brand-800">
            {t("email.settings.viewInbox", "Bekijk verwerkte e-mails →")}
          </Link>
        </p>
      </section>
    </main>
  );
}
